import { logger } from '../lib/logger';

// API Configuration for Super Admin App
// Normalize API_BASE_URL to remove trailing slash
const rawApiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
export const API_BASE_URL = rawApiUrl.replace(/\/+$/, ''); // Remove trailing slashes
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const config = {
  apiBaseUrl: API_BASE_URL,
  supabaseUrl: SUPABASE_URL,
  supabaseAnonKey: SUPABASE_ANON_KEY,
};

// Get auth token from localStorage (set by Supabase auth)
export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const session = localStorage.getItem('sb-auth-token');
  if (session) {
    try {
      const parsed = JSON.parse(session);
      return parsed.access_token || null;
    } catch {
      return null;
    }
  }
  return null;
};

// API request helper
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    logger.apiRequest(options.method || 'GET', `${API_BASE_URL}${endpoint}`);
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      mode: 'cors', // Explicitly set CORS mode
      credentials: 'include', // Include credentials for CORS
    });

    logger.apiResponse(response.status, response.statusText, `${API_BASE_URL}${endpoint}`);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        logger.apiError('API request failed', errorData, `${API_BASE_URL}${endpoint}`);
        
        // Handle validation errors with detailed messages
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.message) return err.message;
            if (err.msg) return err.msg;
            if (err.field && err.message) return `${err.field}: ${err.message}`;
            return JSON.stringify(err);
          }).join('\n');
          errorMessage = `${errorData.message || errorData.error || 'Validation failed'}\n\nValidation errors:\n${validationErrors}`;
        } else {
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      
      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Unauthorized. Please login again.');
      } else if (response.status === 403) {
        throw new Error('Forbidden. You do not have permission to access this resource.');
      } else if (response.status === 404) {
        throw new Error('Resource not found.');
      } else {
        throw new Error(errorMessage);
      }
    }

    return response;
  } catch (error) {
    logger.apiError('API request failed', error, `${API_BASE_URL}${endpoint}`);
    
    // Handle network errors
    if (error instanceof TypeError) {
      if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        const isProduction = API_BASE_URL.startsWith('https://');
        const errorMsg = isProduction 
          ? `Network error: Cannot connect to backend server at ${API_BASE_URL}. Please check:\n1. Backend server is running and accessible\n2. CORS is configured correctly in Railway\n3. Network connection is active\n4. Verify the backend URL is correct`
          : `Network error: Cannot connect to backend server at ${API_BASE_URL}. Please check:\n1. Backend is running on port 8080\n2. CORS is configured correctly\n3. Network connection is active`;
        throw new Error(errorMsg);
      }
      if (error.message.includes('CORS')) {
        const isProduction = API_BASE_URL.startsWith('https://');
        const errorMsg = isProduction
          ? `CORS error: Backend is not allowing requests from this origin. Please check CORS_ORIGIN in Railway includes your frontend URL: ${window.location.origin}`
          : `CORS error: Backend is not allowing requests from this origin. Please check CORS_ORIGIN in Server/.env includes http://localhost:5176`;
        throw new Error(errorMsg);
      }
    }
    
    // Re-throw other errors
    throw error;
  }
};

